import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth, isStaffRole } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

import Home from "@/pages/Home";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Join from "@/pages/Join";
import Download from "@/pages/Download";
import WelcomeMember from "@/pages/WelcomeMember";
import AccountDeletion from "@/pages/AccountDeletion";
import Support from "@/pages/Support";
import StaffLogin from "@/pages/StaffLogin";
import StaffDashboard from "@/pages/StaffDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Community from "@/pages/Community";
import Safety from "@/pages/Safety";
import Partnerships from "@/pages/Partnerships";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
    <div className="w-8 h-8 rounded-full border-2 border-[#E11D48] border-t-transparent animate-spin" />
  </div>
);

// Map marketing subdomains to their page when landing on the root path.
function HostRouter() {
  const navigate = useNavigate();
  useEffect(() => {
    const host = window.location.hostname;
    const path = window.location.pathname;
    if (path !== "/" && path !== "") return;
    if (host.startsWith("download.")) navigate("/download", { replace: true });
    else if (host.startsWith("join.") || host.startsWith("signup.")) navigate("/join", { replace: true });
    else if (host.startsWith("welcome.")) navigate("/welcome-member", { replace: true });
  }, [navigate]);
  return null;
}

// Unlinked staff entry — login when logged out, RBAC gate when logged in.
function StaffEntry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user && !isStaffRole(user)) {
      toast.error("Unauthorized — staff access only");
      navigate("/join", { replace: true });
    }
  }, [user, navigate]);
  if (user === null) return <Spinner />;
  if (user === false) return <StaffLogin />;
  if (isStaffRole(user)) return <Navigate to="/staff/dashboard" replace />;
  return <Spinner />;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user && !isStaffRole(user)) {
      toast.error("Unauthorized — staff access only");
      navigate("/join", { replace: true });
    }
  }, [user, navigate]);
  if (user === null) return <Spinner />;
  if (user === false) return <Navigate to="/staff" replace />;
  if (!isStaffRole(user)) return <Spinner />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <HostRouter />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/members" element={<Navigate to="/terms#subscriptions" replace />} />
            <Route path="/join" element={<Join />} />
            <Route path="/download" element={<Download />} />
            <Route path="/welcome-member" element={<WelcomeMember />} />
            <Route path="/checkout/success" element={<WelcomeMember />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />
            <Route path="/account/delete" element={<AccountDeletion />} />
            <Route path="/delete-account" element={<AccountDeletion />} />
            <Route path="/delete" element={<AccountDeletion />} />
            <Route path="/support" element={<Support />} />
            <Route path="/community" element={<Community />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/partnerships" element={<Partnerships />} />
            <Route path="/staff" element={<StaffEntry />} />
            <Route path="/staff/login" element={<StaffEntry />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/staff/dashboard"
              element={
                <ProtectedRoute>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
